import React from "react";
import "@/App.css";
import { WhatsAppChat, OwnerChat } from "./components/WhatsAppChat";
import SagentLogo from "./components/SagentLogo";
import LogoCloud from "./components/LogoCloud";
import DashboardPreview from "./components/DashboardPreview";
import BentoGrid from "./components/BentoGrid";
import BookingEngine from "./components/BookingEngine";
import CalendarSync from "./components/CalendarSync";
import { Button } from "./components/ui/button";
import { Card, CardContent } from "./components/ui/card";
import { 
  MessageSquare, 
  Zap, 
  Calendar, 
  TrendingUp, 
  UserCheck, 
  Clock,
  AlertCircle,
  BarChart3,
  Smartphone,
  Home,
  Building2,
  TreePine,
  Palmtree,
  Hotel,
  ArrowRight,
  CheckCircle2,
  Star,
  ChevronRight,
  ShieldCheck,
  Phone
} from "lucide-react";

// Hero Chat Messages
const heroMessages = [
  { text: "Hi, is the villa available this weekend for 8 people?", isUser: true, time: "10:28" },
  { text: "Yes, the villa is available on Saturday and Sunday.\n\nThe price for 8 guests is ₹18,000 per night.\n\nWould you like me to reserve it?", isUser: false, time: "10:28" },
  { text: "Can you do ₹16,000?", isUser: true, time: "10:30" },
  { text: "I can offer ₹17,000 including pool access and campfire.\n\nShall I confirm the booking?", isUser: false, time: "10:30" },
];

// Availability Chat Messages
const availabilityMessages = [
  { text: "Is the villa available on 15 March?", isUser: true, time: "09:15" },
  { text: "Yes, the villa is available on 15 March.\n\nThe price is ₹18,000 per night for up to 10 guests.\n\nWould you like me to block the date for you?", isUser: false, time: "09:15" },
];

// Owner Control Messages
const ownerMessages = [
  { text: "Show enquiries today", isOwner: true, time: "14:20" },
  { text: "You received 12 enquiries today\n\n✓ 3 bookings confirmed\n⟲ 5 price negotiations\n⏳ 4 pending follow-ups", isOwner: false, time: "14:20" },
  { text: "Show calendar this weekend", isOwner: true, time: "14:21" },
  { text: "Saturday – Booked\nSunday – Available", isOwner: false, time: "14:21" },
  { text: "Block Sunday", isOwner: true, time: "14:22" },
  { text: "Sunday has been blocked successfully.", isOwner: false, time: "14:22" },
];

// Navigation Component
const Navigation = () => (
  <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-black/5" data-testid="navigation">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between h-16">
        <a href="#" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
          <SagentLogo className="w-8 h-8 text-cyan-600" />
          <span className="text-xl font-bold text-slate-900 font-['Lexend']">Sagent</span>
        </a>
        <div className="hidden md:flex items-center gap-6">
          <a href="#features" className="text-slate-600 hover:text-cyan-600 transition-colors text-sm font-medium">Features</a>
          <a href="#booking-engine" className="text-slate-600 hover:text-cyan-600 transition-colors text-sm font-medium">Booking Engine</a>
          <a href="#calendar-sync" className="text-slate-600 hover:text-cyan-600 transition-colors text-sm font-medium">Calendar Sync</a>
          <a href="#how-it-works" className="text-slate-600 hover:text-cyan-600 transition-colors text-sm font-medium">How It Works</a>
          <a href="#testimonials" className="text-slate-600 hover:text-cyan-600 transition-colors text-sm font-medium">Reviews</a>
          <a href="#contact" className="text-slate-600 hover:text-cyan-600 transition-colors text-sm font-medium">Contact</a>
        </div>
        <a 
          href="#contact"
          data-testid="nav-cta-btn"
        >
          <Button className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-full px-5 btn-lift text-sm font-semibold">
            Get Started
          </Button>
        </a>
      </div>
    </div>
  </nav>
);

// Hero Section
const HeroSection = () => (
  <section className="min-h-[90vh] pt-32 pb-20 relative overflow-hidden bg-grid bg-[#f8fafc]" data-testid="hero-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
        {/* Left content */}
        <div className="space-y-6 animate-fade-in-up">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-600/10 border border-cyan-600/20 text-cyan-700 text-xs font-bold tracking-widest uppercase mb-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-600 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-600"></span>
            </span>
            Next-Gen AI Sales Agent
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-[1.1] tracking-tight font-['Lexend']">
            Automate Your Villa Sales on <span className="text-cyan-600">WhatsApp</span>
          </h1>
          
          <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-lg">
            Sagent handles enquiries instantly, negotiates pricing, and fills your calendar 24/7. Stop losing leads to slow replies.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 pt-2">
            <a 
              href="#contact"
              data-testid="hero-primary-cta"
            >
              <Button className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-full px-7 py-6 text-base font-bold btn-lift shimmer-btn w-full sm:w-auto shadow-lg shadow-cyan-600/20">
                Get Started
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
            <a 
              href="#contact" 
              data-testid="hero-secondary-cta"
            >
              <Button variant="outline" className="border-slate-200 text-slate-600 hover:bg-white hover:text-cyan-600 rounded-full px-7 py-6 text-base font-semibold w-full sm:w-auto glass">
                See Demo
                <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
            </a>
          </div>
        </div>
        
        {/* Right - WhatsApp Mockup */}
        <div className="relative animate-fade-in-up delay-300 flex justify-center">
          <div className="absolute -inset-10 bg-cyan-600/5 blur-[80px] rounded-full" />
          <div className="animate-float relative z-10 scale-95 lg:scale-100 flex justify-center w-full">
            <WhatsAppChat messages={heroMessages} className="shadow-2xl shadow-cyan-600/10 mx-auto" />
          </div>
        </div>
      </div>

      <LogoCloud />
    </div>
  </section>
);

// Problem Section
const ProblemSection = () => (
  <section id="problem" className="py-24 md:py-32 bg-white" data-testid="problem-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <p className="text-cyan-600 font-mono text-sm tracking-[0.3em] uppercase font-bold">
          The Challenge
        </p>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight font-['Lexend']">
          Villa Owners Miss <span className="text-cyan-600">70%</span> of Enquiries
        </h2>
      </div>
      
      <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
        {/* Card 1 - Late Replies */}
        <Card className="border-black/5 shadow-xl shadow-slate-200/50 card-hover bg-white rounded-3xl overflow-hidden" data-testid="problem-card-1">
          <CardContent className="p-8">
            <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mb-6">
              <Clock className="w-7 h-7 text-red-500" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-3 font-['Lexend']">Late Replies</h3>
            <p className="text-slate-600 leading-relaxed text-sm">
              Most enquiries come on WhatsApp or Instagram. If you don't reply instantly, customers move to the next villa.
            </p>
          </CardContent>
        </Card>
        
        {/* Card 2 - Constant Negotiation */}
        <Card className="border-black/5 shadow-xl shadow-slate-200/50 card-hover bg-white rounded-3xl overflow-hidden" data-testid="problem-card-2">
          <CardContent className="p-8">
            <div className="w-14 h-14 rounded-2xl bg-amber-50 flex items-center justify-center mb-6">
              <MessageSquare className="w-7 h-7 text-amber-500" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-3 font-['Lexend']">Constant Negotiation</h3>
            <p className="text-slate-600 leading-relaxed text-sm">
              Owners spend hours answering the same questions about price, rules, availability.
            </p>
          </CardContent>
        </Card>
        
        {/* Card 3 - No Tracking */}
        <Card className="border-black/5 shadow-xl shadow-slate-200/50 card-hover bg-white rounded-3xl overflow-hidden" data-testid="problem-card-3">
          <CardContent className="p-8">
            <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-6">
              <AlertCircle className="w-7 h-7 text-slate-500" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-3 font-['Lexend']">No Enquiry Tracking</h3>
            <p className="text-slate-600 leading-relaxed text-sm">
              Most villa owners have no idea how many leads they receive daily.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  </section>
);

// Dashboard Section
const DashboardSection = () => <DashboardPreview />;

// Features Section
const FeaturesSection = () => <BentoGrid />;

// Live Availability Section (Removed in favor of interactive BookingEngine & CalendarSync components)

// Owner Control Section
const OwnerControlSection = () => (
  <section className="py-24 md:py-32 bg-white" data-testid="owner-control-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <div className="space-y-8">
          <div>
            <p className="text-cyan-600 font-mono text-sm tracking-widest uppercase mb-4 font-bold">
              Owner Dashboard
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight font-['Lexend'] leading-tight">
              Run Your Villa <br className="hidden sm:block" />From <span className="text-cyan-600">WhatsApp</span>
            </h2>
          </div>
          
          <p className="text-lg text-slate-600 leading-relaxed max-w-lg">
            Check enquiries, view bookings, block dates, and manage everything — all through simple WhatsApp messages to Sagent.
          </p>
          
          <div className="grid grid-cols-2 gap-4">
            {[
              { label: "View Enquiries", icon: MessageSquare },
              { label: "Check Calendar", icon: Calendar },
              { label: "Block Dates", icon: Clock },
              { label: "Sales Reports", icon: BarChart3 },
            ].map((item, index) => (
              <div key={index} className="glass rounded-2xl p-4 flex items-center gap-3 border-black/5 shadow-sm hover:shadow-md transition-all">
                <div className="w-8 h-8 rounded-lg bg-cyan-600/10 flex items-center justify-center">
                  <item.icon className="w-4 h-4 text-cyan-600" />
                </div>
                <span className="text-slate-700 text-sm font-bold">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
        
        <div className="relative flex justify-center">
          <div className="absolute -inset-10 bg-cyan-600/5 blur-[80px] rounded-full" />
          <OwnerChat messages={ownerMessages} className="shadow-2xl shadow-cyan-600/10 relative z-10 mx-auto" />
        </div>
      </div>
    </div>
  </section>
);

// Daily Reports Section
const DailyReportsSection = () => (
  <section className="py-24 md:py-32 bg-slate-50" data-testid="reports-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16 space-y-4">
        <p className="text-cyan-600 font-mono text-sm tracking-[0.3em] uppercase font-bold">
          Analytics
        </p>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight font-['Lexend']">
          Daily Enquiry <span className="text-cyan-600">Reports</span>
        </h2>
      </div>
      
      <div className="max-w-3xl mx-auto">
        <Card className="border-black/5 shadow-2xl shadow-slate-200/50 overflow-hidden rounded-3xl" data-testid="stats-card">
          <div className="bg-cyan-600 p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white font-['Lexend'] tracking-tight">Today's Performance</h3>
            </div>
          </div>
          <CardContent className="p-10 bg-white">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
              {[
                { label: "Enquiries", value: "14", color: "text-slate-900" },
                { label: "Bookings", value: "4", color: "text-green-600" },
                { label: "Conversion", value: "28%", color: "text-cyan-600" },
                { label: "Revenue", value: "₹72K", color: "text-slate-900" },
              ].map((stat, index) => (
                <div key={index} className="text-center space-y-1">
                  <p className={`text-xl sm:text-2xl md:text-3xl font-extrabold ${stat.color} font-['Lexend'] tracking-tighter`}>
                    {stat.value}
                  </p>
                  <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">{stat.label}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        
        <p className="text-center text-slate-600 mt-8">
          Sagent automatically tracks every enquiry and booking.
        </p>
      </div>
    </div>
  </section>
);

// How It Works Section
const HowItWorksSection = () => (
  <section id="how-it-works" className="py-24 md:py-32 bg-white" data-testid="how-it-works-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16 space-y-4">
        <p className="text-cyan-600 font-mono text-sm tracking-[0.3em] uppercase font-bold">
          Getting Started
        </p>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight font-['Lexend']">
          Three Steps to <span className="text-cyan-600">Automation</span>
        </h2>
      </div>
      
      <div className="grid md:grid-cols-3 gap-8">
        {[
          {
            step: "01",
            title: "Connect WhatsApp",
            description: "Link your business WhatsApp number to Sagent in minutes.",
            icon: Smartphone,
          },
          {
            step: "02",
            title: "Sync Your Calendar",
            description: "Connect your Google Calendar or booking system.",
            icon: Calendar,
          },
          {
            step: "03",
            title: "Start Selling",
            description: "Sit back while Sagent converts enquiries into bookings.",
            icon: Zap,
          },
        ].map((item, index) => (
          <div key={index} className="relative group" data-testid={`step-${index + 1}`}>
            <div className="bg-white rounded-[2rem] p-8 sm:p-10 h-full border border-black/5 shadow-xl shadow-slate-200/50 group-hover:shadow-cyan-600/5 transition-all duration-500">
              <div className="text-5xl sm:text-6xl font-extrabold text-slate-100 font-['Lexend'] mb-6 group-hover:text-cyan-500/10 transition-colors">
                {item.step}
              </div>
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-cyan-600/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <item.icon className="w-6 h-6 sm:w-7 sm:h-7 text-cyan-600" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-3 font-['Lexend'] tracking-tight">
                {item.title}
              </h3>
              <p className="text-slate-600 leading-relaxed text-sm">
                {item.description}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Perfect For Section
const PerfectForSection = () => (
  <section className="py-24 md:py-32 bg-slate-50" data-testid="perfect-for-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16 space-y-4">
        <p className="text-cyan-600 font-mono text-sm tracking-[0.3em] uppercase font-bold">
          Use Cases
        </p>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight font-['Lexend']">
          Perfect <span className="text-cyan-600">For</span>
        </h2>
      </div>
      
      <div className="flex flex-wrap justify-center gap-4 md:grid md:grid-cols-3 lg:grid-cols-5 lg:gap-6">
        {[
          { icon: TreePine, label: "Farmhouses" },
          { icon: Building2, label: "Luxury Villas" },
          { icon: Home, label: "Airbnb, Booking, MMT & Agoda Hosts" },
          { icon: Hotel, label: "Resorts" },
          { icon: Palmtree, label: "Holiday Homes" },
        ].map((item, index) => (
          <Card key={index} className="w-[calc(50%-8px)] md:w-auto border-black/5 shadow-sm card-hover bg-white rounded-2xl" data-testid={`property-type-${index}`}>
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 rounded-xl bg-cyan-50 flex items-center justify-center mx-auto mb-4">
                <item.icon className="w-6 h-6 text-cyan-600" />
              </div>
              <p className="font-medium text-slate-900">{item.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  </section>
);

// Testimonials Section
const TestimonialsSection = () => (
  <section id="testimonials" className="py-24 md:py-32 bg-slate-50" data-testid="testimonials-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16 space-y-4">
        <p className="text-cyan-600 font-mono text-sm tracking-[0.3em] uppercase font-bold">
          Testimonials
        </p>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight font-['Lexend']">
          Why Owners <span className="text-cyan-600">Love Sagent</span>
        </h2>
      </div>
      
      <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
        {[
          {
            quote: "Sagent replies faster than my staff ever could. We've seen a 40% jump in weekend bookings.",
            name: "Rahul Mehra",
            role: "Owner, The White Pebble Villa",
          },
          {
            quote: "The price negotiation feature is brilliant. It handles discounts exactly how I would.",
            name: "Priya Sharma",
            role: "Manager, Azure Palms Resort",
          },
          {
            quote: "The dashboard gives me peace of mind. I can see Sagent closing deals while I'm traveling.",
            name: "Amit Kapoor",
            role: "Owner, Heritage Hills Estate",
          },
        ].map((testimonial, index) => (
          <Card key={index} className="border-black/5 bg-white shadow-sm hover:shadow-lg hover:shadow-cyan-600/5 transition-all duration-500 rounded-3xl" data-testid={`testimonial-${index}`}>
            <CardContent className="p-8">
              <div className="flex gap-1 mb-6">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-base text-slate-600 mb-6 leading-relaxed">
                "{testimonial.quote}"
              </p>
              <div className="flex items-center gap-3 pt-6 border-t border-black/5">
                <div className="w-10 h-10 rounded-full bg-cyan-600/10 flex items-center justify-center">
                  <span className="text-cyan-600 font-bold text-sm">
                    {testimonial.name.charAt(0)}
                  </span>
                </div>
                <div>
                  <p className="font-bold text-slate-900 font-['Lexend'] tracking-tight text-sm">{testimonial.name}</p>
                  <p className="text-[10px] text-slate-400 font-mono uppercase tracking-widest font-bold">{testimonial.role}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  </section>
);

// Security & Trust Section
const SecuritySection = () => (
  <section className="py-24 relative overflow-hidden bg-white">
    <div className="absolute inset-0 bg-grid-small opacity-5" />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <div className="glass-premium p-8 sm:p-12 rounded-[2.5rem] sm:rounded-[3rem] border-black/5 overflow-hidden relative shadow-2xl shadow-cyan-900/5">
        <div className="absolute top-0 right-0 p-8 opacity-5">
          <ShieldCheck className="w-64 h-64 text-cyan-600" />
        </div>
        
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 font-['Lexend'] tracking-tight">
              Enterprise-Grade <span className="text-cyan-600">Security</span>
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed">
              Your villa's data and guest privacy are our top priorities. Sagent is built on a 
              foundation of security and reliability.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 pt-4">
              {[
                { title: "End-to-End Encrypted", desc: "All guest conversations are secure." },
                { title: "GDPR Compliant", desc: "Highest standards of data privacy." },
                { title: "Secure Sync", desc: "Read-only calendar access options." },
                { title: "99.9% Uptime", desc: "Reliable 24/7 sales coverage." },
              ].map((item, i) => (
                <div key={i} className="space-y-1">
                  <h4 className="text-slate-900 font-bold text-sm flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-cyan-600" />
                    {item.title}
                  </h4>
                  <p className="text-slate-500 text-[10px] sm:text-xs">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
          
          <div className="bg-white rounded-3xl p-8 border border-black/5 shadow-xl backdrop-blur-md">
             <div className="flex items-center gap-4 mb-6">
               <div className="w-12 h-12 rounded-full bg-cyan-600/10 flex items-center justify-center">
                 <ShieldCheck className="w-6 h-6 text-cyan-600" />
               </div>
               <div>
                 <p className="text-slate-900 font-bold tracking-tight">Trusted Security</p>
                 <p className="text-slate-500 text-[10px] uppercase font-bold tracking-widest">Verified Infrastructure</p>
               </div>
             </div>
             <p className="text-slate-600 text-sm leading-relaxed mb-6 font-medium">
               "We prioritize data integrity. Sagent uses the same infrastructure trusted by 
               global hospitality brands to ensure your bookings are handled safely."
             </p>
             <div className="flex gap-3">
                <div className="h-8 w-24 bg-slate-100 rounded flex items-center justify-center text-[10px] text-slate-500 font-mono tracking-tighter border border-slate-200">SSL SECURE</div>
                <div className="h-8 w-24 bg-slate-100 rounded flex items-center justify-center text-[10px] text-slate-500 font-mono tracking-tighter border border-slate-200">256-BIT AES</div>
             </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Get In Touch Section
const GetInTouchSection = () => (
  <section id="contact" className="py-24 md:py-32 relative overflow-hidden bg-white" data-testid="contact-section">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
      <div className="space-y-4 mb-16">
        <h2 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold text-slate-900 tracking-tight font-['Lexend'] leading-tight">
          Get in touch
        </h2>
        <p className="text-base sm:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed px-4">
          Ready to transform your villa operations?<br />
          Start your free trial and experience the future.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 md:gap-8 mt-12">
        {/* Phone Card */}
        <a href="tel:+919676038012" className="group">
          <Card className="border-black/5 bg-white shadow-xl shadow-slate-100/50 rounded-[2rem] sm:rounded-[2.5rem] p-8 sm:p-10 h-full flex flex-col items-center justify-center card-hover transition-all duration-500 hover:shadow-cyan-600/10">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl sm:rounded-3xl bg-cyan-50 flex items-center justify-center mb-6 sm:mb-8 group-hover:scale-110 transition-transform duration-500">
              <Phone className="w-8 h-8 sm:w-10 sm:h-10 text-cyan-600" fill="currentColor" fillOpacity="0.1" />
            </div>
            <p className="text-[10px] font-mono font-bold tracking-[0.3em] text-slate-400 uppercase mb-3">Phone</p>
            <p className="text-xl sm:text-2xl md:text-3xl font-extrabold text-cyan-600 font-['Lexend'] tracking-tight">
              +91 9676038012
            </p>
          </Card>
        </a>

        {/* Email Card */}
        <a href="mailto:tejavath@snowtable.in" className="group">
          <Card className="border-black/5 bg-white shadow-xl shadow-slate-100/50 rounded-[2rem] sm:rounded-[2.5rem] p-8 sm:p-10 h-full flex flex-col items-center justify-center card-hover transition-all duration-500 hover:shadow-cyan-600/10">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl sm:rounded-3xl bg-cyan-50 flex items-center justify-center mb-6 sm:mb-8 group-hover:scale-110 transition-transform duration-500">
              <MessageSquare className="w-8 h-8 sm:w-10 sm:h-10 text-cyan-600" fill="currentColor" fillOpacity="0.1" />
            </div>
            <p className="text-[10px] font-mono font-bold tracking-[0.3em] text-slate-400 uppercase mb-3">Email</p>
            <p className="text-base sm:text-xl md:text-2xl font-extrabold text-cyan-600 font-['Lexend'] tracking-tight break-all px-4">
              tejavath@snowtable.in
            </p>
          </Card>
        </a>
      </div>
    </div>
  </section>
);

// Footer
const Footer = () => (
  <footer className="bg-slate-50 py-16 border-t border-slate-200" data-testid="footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col md:flex-row items-center justify-between gap-10">
        <a href="#" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
          <SagentLogo className="w-8 h-8 text-cyan-600" />
          <span className="text-xl font-bold text-slate-900 font-['Lexend'] tracking-tight">Sagent</span>
        </a>
        
        <p className="text-slate-500 text-sm font-medium">
          © 2026 Snowtable Hospitality Technologies Private Limited. Handcrafted for premium hospitality.
        </p>
        
        <div className="flex items-center gap-8">
          <a href="#" className="text-slate-500 hover:text-cyan-600 text-sm font-semibold transition-colors">Privacy</a>
          <a href="#" className="text-slate-500 hover:text-cyan-600 text-sm font-semibold transition-colors">Terms</a>
          <a href="#contact" className="text-slate-500 hover:text-cyan-600 text-sm font-semibold transition-colors">Contact</a>
        </div>
      </div>
    </div>
  </footer>
);

// Main App Component
function App() {
  return (
    <div className="App" data-testid="sagent-landing">
      <Navigation />
      <HeroSection />
      <ProblemSection />
      <DashboardSection />
      <FeaturesSection />
      <BookingEngine />
      <CalendarSync />
      <OwnerControlSection />
      <DailyReportsSection />
      <HowItWorksSection />
      <PerfectForSection />
      <TestimonialsSection />
      <SecuritySection />
      <GetInTouchSection />
      <Footer />
    </div>
  );
}

export default App;
